import React, { useState, useCallback, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';

import './Style.css';
import AccessDenied from '../../shared/components/accessDenied/AccessDenied';
import LoadingSpinner from '../../shared/components/UIElements/LoadingSpinner';
import { AuthContext } from '../../shared/context/auth-context';
import { Header } from '../../shared/headerNavigation/header/Header';
import { useAuth } from '../../shared/hooks/auth-hook';
import { useHttpClient } from '../../shared/hooks/http-hook';
import { useMedia } from '../../shared/hooks/media-hook';
import { useUserData } from '../../shared/hooks/useUserData';
import Modal from '../../shared/modal/Modal';
import SideMenu from '../menu/Menu';
import CoRepFilter from './components/CoRepFilter';
import Campaigns from './sections/CampaignsComms';
import Engagement from './sections/EngagementResources';
import ErvImport from './sections/ErvImport';
import HealthWellbeing from './sections/HealthWellbeingStrategy';
import Leadership from './sections/LeadershipLab';
import LiveAnalytics from './sections/LiveAnalytics';
import Partners from './sections/PartnersProviders';

export function CompanyReport() {
  const { t } = useTranslation();
  const { isLoading, sendRequest } = useHttpClient();
  const { userData } = useUserData();

  const { tags } = useAuth();
  const { openMediaModal, favourites, progressList, scrolled } = useMedia();
  const auth = useContext(AuthContext);

  const [threshold, setThreshold] = useState(15);
  const [scores, setScores] = useState();
  const [coData, setCoData] = useState();
  const [companyName, setCompanyName] = useState();
  const [reportPeriod, setReportPeriod] = useState();
  // loadFailed outlives the modal: dismissing the message must not make the page look
  // like it is still loading, nor block re-applying the selection that failed
  const [loadFailed, setLoadFailed] = useState(false);
  const [failureModalOpen, setFailureModalOpen] = useState(false);

  const [filtersArr, setFiltersArr] = useState([]);
  const [allowedFilters, setAllowedFilters] = useState();
  const [filterSnapshot, setFilterSnapshot] = useState();

  const latestRequestId = useRef(0);
  const lastRequestedPeriod = useRef(undefined);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const getDataForSetPeriod = (period) => {
    // setFiltersArr([]);
    fetchData(period);
  };

  const handleFailure = () => {
    setLoadFailed(true);
    setFailureModalOpen(true);
    // two modals would stack, so the filter one steps aside for the failure message
    setFilterOpen(false);
  };

  const fetchData = async (period) => {
    // nothing cancels an in-flight report request, and the endpoint is slow enough that a
    // filter change routinely overlaps the previous one, so only the newest may write state
    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;
    // the retry button replays this, so a period-specific failure retries that same period
    lastRequestedPeriod.current = period;

    setLoadFailed(false);
    setFailureModalOpen(false);
    try {
      const formData = new FormData();
      formData.append('userid', auth.userId);
      formData.append('filtersArr', filtersArr);
      formData.append('periodid', period);
      if (queryParams.get('requestedClientId')) {
        formData.append('requestedClientId', queryParams.get('requestedClientId'));
      }

      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/assessments/datarequest`,
        'POST',
        formData,
        {} // Keep to ensure it ensure doesn't send Content-Type
      );
      if (requestId !== latestRequestId.current) return;

      if (!responseData) {
        handleFailure();
        return;
      }

      if (responseData.leadership_threshold) {
        setThreshold(responseData.leadership_threshold);
      }
      setScores(responseData.scores);
      // if (!period && !coData) {
      setCoData(responseData.codata);
      // }
      setCompanyName(responseData.client_name);
      // check current report period if different or no filters
      // setFiltersArr(responseData.codata.applied_filters);
      // the response omits applied_filters entirely when no filters are applied
      if (
        (filtersArr.length < 1 && !allowedFilters) ||
        (filtersArr.length < 1 && !reportPeriod) ||
        (filtersArr.length < 1 && reportPeriod && reportPeriod !== responseData.report_period) ||
        (responseData.codata.applied_filters?.length ?? 0) < 1
      ) {
        setAllowedFilters(responseData.codata.allowed_filters);
      }
      setReportPeriod(responseData.codata.report_period);
    } catch (err) {
      if (requestId !== latestRequestId.current) return;

      handleFailure();
    }
  };

  const { pageid } = useParams();
  const { subpageid } = useParams();

  useEffect(() => {
    if (pageid !== 'live-analytics' || !auth.userId) return;
    fetchData();
    // fetchData is re-created every render and sets the state it reads, so
    // depending on it would loop the main report fetch. The listed deps are
    // the deliberate refetch triggers; the auth context object itself changes
    // identity several times while auth hydrates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageid, filtersArr, auth.userId]);

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalTextContent, setModalTextContent] = useState('');

  const modalClickHandler = useCallback(() => {
    setModalIsOpen(false);
  }, []);

  const openModal = useCallback((props) => {
    const modalText =
      props ||
      'If engagement is low and you would like to extend your health assessment window, please contact one of the Champion team.';
    setModalIsOpen(true);
    // if (typeof props === "object") {
    //   props = "Not currently available.";
    // }
    setModalTextContent(modalText);
  }, []);

  window.onscroll = function handleWindowScroll() {
    const scrollPosition =
      window.pageYOffset !== undefined
        ? window.pageYOffset
        : (document.documentElement || document.body.parentNode || document.body).scrollTop;
    if (document.getElementsByClassName('scrollLinks').length > 0) {
      if (scrollPosition > 160) {
        document.getElementsByClassName('scrollLinks')[0].classList.add('scrolled');
      } else {
        document.getElementsByClassName('scrollLinks')[0].classList.remove('scrolled');
      }
    }
  };

  useEffect(() => {
    if (scrolled) {
      window.scrollTo(0, scrolled);
    }
  }, [scrolled]);

  const onChangeSliderPos = (props) => {
    const current = sliderPos;
    current[props.id] = props.pos;
    setSliderPos(current);
  };

  const [sliderPos, setSliderPos] = useState({});

  // filter
  const [filterOpen, setFilterOpen] = useState(false);

  const toggleFilterOpen = () => {
    setFilterOpen(!filterOpen);
  };

  const filterSelectHandler = (selectedFiltersArr, filterObj) => {
    // filtersArr reaches the API comma-joined, so an identical join is an identical
    // request and re-running it would rebuild the whole report for nothing. After a
    // failure it holds what was requested rather than what loaded, so re-applying the
    // same selection has to stay possible - that is the route the alert points at.
    if (loadFailed || selectedFiltersArr.join(',') !== filtersArr.join(',')) {
      setFiltersArr(selectedFiltersArr);
    }
    setFilterSnapshot(filterObj);
    // always close: the request fires from here, and the failure alert renders on the
    // page underneath, so leaving the modal open would hide it
    setFilterOpen(false);
    window.scrollTo(0, 0);
  };

  // auth hydrates after the first render, so until access is known it must not be read
  // as a denial - otherwise AccessDenied flashes before the report appears
  const accessFallback = auth.access ? <AccessDenied /> : <LoadingSpinner asOverlay />;

  // retrying only helps the initial load; a failed refresh is re-run from the filter
  // panel, where the selection is still visible, so the modal's own close is the action
  const failureActionDetails = scores
    ? { description: 'companyReport.loadFailed.staleDescription', showRetry: false }
    : { description: 'companyReport.loadFailed.description', showRetry: true };

  return (
    <div id="main_container" className="reports company_report">
      {filterOpen && (
        <CoRepFilter
          threshold={threshold}
          coData={coData}
          filterSelectHandler={filterSelectHandler}
          getDataForSetPeriod={getDataForSetPeriod}
          toggleFilterOpen={toggleFilterOpen}
          filtersArr={filtersArr}
          allowedFilters={allowedFilters}
          filterSnapshot={filterSnapshot}
        />
      )}
      {modalIsOpen && (
        <Modal
          modalIsOpen={modalIsOpen}
          onModalClose={modalClickHandler}
          modalText={modalTextContent}
        />
      )}
      <Header page="company" type="transparent" />
      {parseInt(auth.access, 10) > 2 && !auth.isHub ? (
        <>
          <div id="left_container">
            <SideMenu
              coData={coData}
              showLiveAnalyticsOnly={!!queryParams.get('requestedClientId')}
            />
          </div>
          <div id="right_container">
            {pageid === 'live-analytics' && !scores && !loadFailed && <LoadingSpinner asOverlay />}

            {pageid === 'live-analytics' && failureModalOpen && (
              <Modal
                modalIsOpen
                isError
                onModalClose={() => setFailureModalOpen(false)}
                questionTitle={t('companyReport.loadFailed.title')}
                modalText={<div>{t(failureActionDetails.description)}</div>}
                primaryAction={
                  failureActionDetails.showRetry
                    ? {
                        label: t('companyReport.loadFailed.retry'),
                        onClick: () => fetchData(lastRequestedPeriod.current),
                      }
                    : undefined
                }
              />
            )}

            {pageid === 'live-analytics' && !subpageid && scores && coData && (
              <LiveAnalytics
                disableFilters={userData?.customFeatureClient === 'pam'}
                threshold={threshold}
                onOpenModal={openModal}
                scores={scores}
                onOpenMediaModal={openMediaModal}
                coData={coData}
                reportPeriod={reportPeriod}
                getDataForSetPeriod={getDataForSetPeriod}
                isLoading={isLoading}
                filtersArr={filtersArr}
                toggleFilterOpen={toggleFilterOpen}
                filterOpen={filterOpen}
                companyName={companyName}
              />
            )}
            {pageid === 'campaigns-communications' && !subpageid && (
              <Campaigns
                onOpenModal={openModal}
                scores={scores}
                onOpenMediaModal={openMediaModal}
              />
            )}
            {pageid === 'engagement-resources' && !subpageid && (
              <Engagement
                onOpenModal={openModal}
                scores={scores}
                onOpenMediaModal={openMediaModal}
              />
            )}
            {pageid === 'health-wellbeing-strategy' && !subpageid && (
              <HealthWellbeing
                onOpenModal={openModal}
                scores={scores}
                onOpenMediaModal={openMediaModal}
                coData={coData}
              />
            )}
            {pageid === 'leadership-lab' && !subpageid && (
              <Leadership
                onOpenModal={openModal}
                scores={scores}
                onOpenMediaModal={openMediaModal}
                tags={tags}
                favourites={favourites}
                progressList={progressList}
                sliderPos={sliderPos}
                onChangeSliderPos={onChangeSliderPos}
                scrolled={scrolled}
              />
            )}
            {pageid === 'partners-providers' && !subpageid && (
              <Partners onOpenModal={openModal} scores={scores} onOpenMediaModal={openMediaModal} />
            )}
            {pageid === 'erv-import' && !subpageid && <ErvImport coData={coData} />}
          </div>
        </>
      ) : (
        accessFallback
      )}
    </div>
  );
}
